import {
    Box,
    Text,
    VStack,
    HStack,
    Flex,
    Spinner,
    Center,
    Badge,
    Tooltip,
} from '@chakra-ui/react';
import React, { useState, useMemo, useEffect } from 'react';
import { useGetActivityLogs, EnrichedActivityLogEntry } from '../../../hooks/useGetActivityLogs';
import SourceBadge, {
    toSourceBadgeType,
    type ActivitySourceBadgeType,
} from '../../../components/SourceBadge';
import useCopyToClipboard from '../../../hooks/useCopyToClipboard';
import CopyTooltip from '../../../components/CopyTooltip';
import { SIDE_DRAWER_CONTAINER_PROPS, INSTANT_SIDE_DRAWER_MOTION_PROPS } from '../../../modals/sideDrawerContainerProps';
import Timeline, { TimelineGroupData } from '../../../components/Timeline';
import AppDrawer from '../../../components/AppDrawer';
import {
    ActivityFeedFilters,
    EMPTY_ACTIVITY_FEED_FILTERS,
    hasActiveActivityFeedFilters,
    useActivityFeedFilterUsers,
    type ActivityFeedFiltersValue,
} from '../../../components/activityFeedFilters';
import { Button, Icon as YaxleIcon, TextField, type IconName } from '../../../ui';
import { formatPrice } from '../../../functions';

const PAGE_SIZE = 50;

interface ActivityLogDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
    hideOverlay?: boolean;
}

function formatEventType(eventType: string): string {
    return eventType
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

const EVENT_TYPE_LABELS: Record<string, string> = {
    'order-created': 'Order Created',
    'order-details-edited': 'Order Details Edited',
    'order-tag-changed': 'Tag Changed',
    'quote-request-submitted': 'Quote Request Submitted',
    'quoting-skipped': 'Quoting Skipped',
    'quotes-complete': 'Quoting Complete',
    'part-excluded': 'Part Excluded',
    'part-included': 'Part Included',
    'part-marked-ordered': 'Part Marked Ordered',
    'part-unmarked-ordered': 'Part Marked Unordered',
    'part-re-included': 'Part Re-included',
    'part-excluded-from-quoting': 'Excluded From Quoting',
    'part-quoting-restored': 'Quoting Restored',
    'part-selections-changed': 'Changed Selections',
    'reset-to-partbay-selections': 'Reset To Yaxle Selections',
    'flag-removed': 'Flag Removed',
    'list-price-adjusted': 'List Price Adjusted',
    'option-note-changed': 'Option Note Changed',
    'order-placement-requested': 'Order Requested',
    'orders-complete': 'Order Placed',
    'supplier-rejected': 'Supplier Rejected',
    'supplier-cancelled-parts': 'Supplier Cancelled',
    'supplier-returned-parts': 'Supplier Returned',
    'shop-cancel-or-return': 'Cancel / Return',
    'supplier-changes-accepted': 'Supplier Changes Accepted',
    'supplier-order-confirmed': 'Order Confirmed',
    'order-completed': 'Order Completed',
    'order-uncompleted': 'Order Reopened',
    'order-archived': 'Order Archived',
    'order-unarchived': 'Order Unarchived',
    'parts-received': 'Parts Received',
    'parts-unreceived': 'Receive Undone',
    'workflow-cancelled': 'Workflow Cancelled',
    // Older types no longer emitted, but left to support events produced before 04/16/26
    'auto-order': 'Auto Order (legacy)',
    'request-quotes': 'Request Quotes (legacy)',
    /** @deprecated merged into filter key `order-placed` with `orders-complete` */
    'place-order': 'Order Placed',
};

export const EVENT_TYPE_COLORS: Record<string, string> = {
    'order-created': 'info.fg',
    'order-details-edited': 'brand.solid',
    'order-tag-changed': 'accent.fg',
    'quote-request-submitted': 'info.fg',
    'quoting-skipped': 'fg.muted',
    'quotes-complete': 'brand.solid',
    'part-excluded': 'error.fg',
    'part-included': 'success.fg',
    'part-marked-ordered': 'success.fg',
    'part-unmarked-ordered': 'error.fg',
    'part-re-included': 'success.fg',
    'part-excluded-from-quoting': 'error.fg',
    'part-quoting-restored': 'success.fg',
    'part-selections-changed': 'warning.fg',
    'reset-to-partbay-selections': 'brand.solid',
    'flag-removed': 'success.fg',
    'list-price-adjusted': 'warning.fg',
    'option-note-changed': 'brand.solid',
    'order-placement-requested': 'success.fg',
    'orders-complete': 'success.fg',
    'supplier-rejected': 'error.fg',
    'supplier-cancelled-parts': 'warning.fg',
    'supplier-returned-parts': 'warning.fg',
    'shop-cancel-or-return': 'error.fg',
    'supplier-changes-accepted': 'success.fg',
    'supplier-order-confirmed': 'success.fg',
    'order-completed': 'success.fg',
    'order-uncompleted': 'info.fg',
    'order-archived': 'fg.muted',
    'order-unarchived': 'info.fg',
    'parts-received': 'success.fg',
    'parts-unreceived': 'warning.fg',
    'workflow-cancelled': 'error.fg',
    'auto-order': 'accent.fg',
    'request-quotes': 'info.fg',
    'place-order': 'success.fg',
};

const EVENT_TYPE_YAXLE_ICON: Record<string, IconName> = {
    'order-created': 'fileText',
    'order-details-edited': 'edit',
    'order-tag-changed': 'tag',
    'quote-request-submitted': 'send',
    'quoting-skipped': 'skipForward',
    'quotes-complete': 'dollar',
    'part-excluded': 'circleOff',
    'part-included': 'circlePlus',
    'part-marked-ordered': 'checkCircle',
    'part-unmarked-ordered': 'circleOff',
    'part-re-included': 'circlePlus',
    'part-excluded-from-quoting': 'quoteOff',
    'part-quoting-restored': 'quote',
    'part-selections-changed': 'adjustments',
    'reset-to-partbay-selections': 'restore',
    'flag-removed': 'flagOff',
    'list-price-adjusted': 'dollar',
    'option-note-changed': 'edit',
    'order-placement-requested': 'package',
    'orders-complete': 'shoppingCart',
    'supplier-rejected': 'thumbDown',
    'supplier-cancelled-parts': 'ban',
    'supplier-returned-parts': 'arrowBackUp',
    'shop-cancel-or-return': 'arrowBackUp',
    'supplier-changes-accepted': 'checkBadge',
    'supplier-order-confirmed': 'checkBadge',
    'order-completed': 'checkBadge',
    'order-uncompleted': 'arrowBackUp',
    'order-archived': 'archive',
    'order-unarchived': 'archiveOff',
    'parts-received': 'checkCircle',
    'parts-unreceived': 'arrowBackUp',
    'workflow-cancelled': 'ban',
    'auto-order': 'shoppingCart',
    'request-quotes': 'dollar',
    'place-order': 'shoppingCart',
};

export const EVENT_TYPE_ICON_BG: Record<string, string> = {
    'order-created': 'info.subtle',
    'order-details-edited': 'brand.subtle',
    'order-tag-changed': 'accent.subtle',
    'quote-request-submitted': 'info.subtle',
    'quoting-skipped': 'bg.subtle',
    'quotes-complete': 'brand.subtle',
    'part-excluded': 'error.subtle',
    'part-included': 'success.subtle',
    'part-marked-ordered': 'success.subtle',
    'part-unmarked-ordered': 'error.subtle',
    'part-re-included': 'success.subtle',
    'part-excluded-from-quoting': 'error.subtle',
    'part-quoting-restored': 'success.subtle',
    'part-selections-changed': 'warning.subtle',
    'reset-to-partbay-selections': 'brand.subtle',
    'flag-removed': 'success.subtle',
    'list-price-adjusted': 'warning.subtle',
    'option-note-changed': 'brand.subtle',
    'order-placement-requested': 'success.subtle',
    'orders-complete': 'success.subtle',
    'supplier-rejected': 'error.subtle',
    'supplier-cancelled-parts': 'warning.subtle',
    'supplier-returned-parts': 'warning.subtle',
    'shop-cancel-or-return': 'error.subtle',
    'supplier-changes-accepted': 'success.subtle',
    'supplier-order-confirmed': 'success.subtle',
    'order-completed': 'success.subtle',
    'order-uncompleted': 'info.subtle',
    'order-archived': 'bg.subtle',
    'order-unarchived': 'info.subtle',
    'parts-received': 'success.subtle',
    'parts-unreceived': 'warning.subtle',
    'workflow-cancelled': 'error.subtle',
    'auto-order': 'accent.subtle',
    'request-quotes': 'info.subtle',
    'place-order': 'success.subtle',
};

export function EventTypeIcon({
    eventType,
    viaCarl = false,
    size,
}: {
    eventType: string;
    viaCarl?: boolean;
    size: string | number;
}) {
    const color = viaCarl ? 'brand.solid' : (EVENT_TYPE_COLORS[eventType] ?? 'brand.solid');
    const name: IconName = viaCarl ? 'sparkles' : (EVENT_TYPE_YAXLE_ICON[eventType] ?? 'fileText');
    return <YaxleIcon name={name} color={color} size={size} />;
}

/** Synthetic UI value: expands to `orders-complete` + legacy `place-order` for the API. */
const ORDER_PLACED_FILTER = 'order-placed' as const;

function mapOrderEventTypesForApi(selected: string[]): string[] {
    const out: string[] = [];
    for (const value of selected) {
        if (value === ORDER_PLACED_FILTER) {
            out.push('orders-complete', 'place-order');
        } else {
            out.push(value);
        }
    }
    return out;
}

const EVENT_TYPE_FILTER_GROUPS: { title: string; items: Array<{ value: string; label: string; }>; }[] = [
    {
        title: 'Order',
        items: [
            { value: 'order-created', label: 'Order Created' },
            { value: 'order-details-edited', label: 'Order Details Edited' },
            { value: 'order-tag-changed', label: 'Tag Changed' },
            { value: 'order-completed', label: 'Order Completed' },
            { value: 'order-uncompleted', label: 'Order Reopened' },
            { value: 'order-archived', label: 'Order Archived' },
            { value: 'order-unarchived', label: 'Order Unarchived' },
        ],
    },
    {
        title: 'Quoting',
        items: [
            { value: 'quote-request-submitted', label: 'Quote Request Submitted' },
            { value: 'quoting-skipped', label: 'Quoting Skipped' },
            { value: 'quotes-complete', label: 'Quoting Complete' },
            { value: 'request-quotes', label: 'Request Quotes (legacy)' },
        ],
    },
    {
        title: 'Parts',
        items: [
            { value: 'part-excluded', label: 'Part Excluded' },
            { value: 'part-included', label: 'Part Included' },
            { value: 'part-marked-ordered', label: 'Part Marked Ordered' },
            { value: 'part-unmarked-ordered', label: 'Part Marked Unordered' },
            { value: 'part-excluded-from-quoting', label: 'Excluded From Quoting' },
            { value: 'part-quoting-restored', label: 'Quoting Restored' },
            { value: 'part-selections-changed', label: 'Changed Selections' },
            { value: 'reset-to-partbay-selections', label: 'Reset To Yaxle Selections' },
            { value: 'flag-removed', label: 'Flag Removed' },
            { value: 'list-price-adjusted', label: 'List Price Adjusted' },
            { value: 'option-note-changed', label: 'Option Note Changed' },
        ],
    },
    {
        title: 'Ordering',
        items: [
            { value: 'order-placement-requested', label: 'Order Requested' },
            { value: ORDER_PLACED_FILTER, label: 'Order Placed' },
            { value: 'workflow-cancelled', label: 'Workflow Cancelled' },
            { value: 'supplier-order-confirmed', label: 'Order Confirmed' },
            { value: 'auto-order', label: 'Auto Order (legacy)' },
        ],
    },
    {
        title: 'Supplier & Returns',
        items: [
            { value: 'supplier-rejected', label: 'Supplier Rejected' },
            { value: 'supplier-cancelled-parts', label: 'Supplier Cancelled' },
            { value: 'supplier-returned-parts', label: 'Supplier Returned' },
            { value: 'shop-cancel-or-return', label: 'Cancel / Return' },
        ],
    },
    {
        title: 'Delivery',
        items: [
            { value: 'parts-received', label: 'Parts Received' },
            { value: 'parts-unreceived', label: 'Receive Undone' },
        ],
    },
];

function formatDateGroup(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const logDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (logDate.getTime() === today.getTime()) return 'Today';

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (logDate.getTime() === yesterday.getTime()) return 'Yesterday';

    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(dateStr: string): string {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const time = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${month}/${day} • ${time}`;
}

function getActorName(log: EnrichedActivityLogEntry): string {
    if (!log.userName) {
        // Shop-side actions pushed from the shop's management system are attributed
        // to that system, not to us.
        if (log.source === 'nexsyis') return 'Nexsyis';
        return 'Yaxle';
    }
    return `${log.userName.firstName} ${log.userName.lastName}`;
}

const LEGACY_TAG_DETAIL_FIELD_TO_TAG: Record<string, string> = {
    'Save for Later': 'Save for Later',
    'Save For Later': 'Save for Later',
    'Saved for Later': 'Save for Later',
    'Saved For Later': 'Save for Later',
    'Reviewed By Me': 'Mark Reviewed',
};

function parseBooleanTransition(value: string): 'Added' | 'Removed' | null {
    const match = value.match(/^\s*(true|false)\s*→\s*(true|false)\s*$/i);
    if (!match) return null;
    return match[2].toLowerCase() === 'true' ? 'Added' : 'Removed';
}

/** Legacy rows logged tag flips as `order-details-edited` before `order-tag-changed`. */
function getLegacyTagChangeFromDetailsEdited(
    log: EnrichedActivityLogEntry,
): { tag: string; action: 'Added' | 'Removed'; } | null {
    if (log.eventType !== 'order-details-edited') return null;

    const m = log.metadata as Record<string, string | undefined> | undefined;
    if (!m) return null;

    const detailKey = Object.keys(m).find((key) => LEGACY_TAG_DETAIL_FIELD_TO_TAG[key]);
    if (!detailKey) return null;

    const hasOtherChanges = Object.keys(m).some((key) => key !== detailKey);
    if (hasOtherChanges) return null;

    const action = parseBooleanTransition(m[detailKey] ?? '');
    if (!action) return null;

    return {
        tag: LEGACY_TAG_DETAIL_FIELD_TO_TAG[detailKey],
        action,
    };
}

function getDisplayEventType(log: EnrichedActivityLogEntry): string {
    if (log.eventType === 'part-excluded' && log.metadata?.['Action']?.startsWith('Re-included')) {
        return 'part-re-included';
    }
    if (log.eventType === 'order-tag-changed' || getLegacyTagChangeFromDetailsEdited(log)) {
        return 'order-tag-changed';
    }
    return log.eventType;
}

function getActionLabel(log: EnrichedActivityLogEntry): string {
    const displayType = getDisplayEventType(log);
    return EVENT_TYPE_LABELS[displayType] || formatEventType(log.eventType);
}

/**
 * Color schemes for order tag badges, mirroring `components/OrderTag` so a tag
 * shown in the activity timeline matches how it renders in the order header.
 * Keyed by the human label the backend stamps onto `order-tag-changed`
 * metadata (e.g. "Quotes Only", "Potential Total", "Save for Later", "Mark Reviewed").
 */
function getOrderTagColorScheme(tagLabel: string): string {
    switch (tagLabel) {
        case 'Quotes Only': return 'teal';
        case 'Potential Total': return 'yellow';
        case 'Dealer Referral': return 'cyan';
        case 'OEM Endorsement': return 'blue';
        case 'OEM Certification': return 'purple';
        case 'Save for Later':
        case 'Save For Later':
        case 'Saved for Later':
        case 'Saved For Later':
        case 'Manually Flagged': return 'orange';
        case 'Mark Reviewed':
        case 'Reviewed By Me': return 'purple';
        case 'Completed': return 'green';
        case 'Archived': return 'gray';
        default: return 'gray';
    }
}

function getTypeColorScheme(type: string): string {
    return type === 'OEM' ? 'blue'
        : type === 'RCY' ? 'green'
            : type === 'A/M' ? 'orange'
                : type === 'RE-CON' ? 'purple'
                    : type === 'RE-MAN' ? 'teal'
                        : type === 'OE Surplus' ? 'cyan'
                            : 'gray';
}

/**
 * Color scheme for the plain-text fallback badge used when a Source value
 * isn't recognized by `toSourceBadgeType` (e.g. `Manual`). Known marketplace
 * sources render the `SourceBadge` icon instead.
 */
function getSourceColorScheme(src: string): string {
    return src === 'PT' || src === 'PartsTrader' ? 'blue'
        : src === 'CCC' ? 'cyan'
            : src === 'CCC-SP' || src === 'ccc-sp' ? 'cyan'
                : src === 'OT' || src === 'OpsTrax' ? 'gray'
                    : src === 'Tesla' ? 'red'
                        : src === 'Mitchell' || src === 'mitchell' ? 'orange'
                            : src === 'Manual' ? 'purple'
                                : 'gray';
}

/**
 * Builds a "<count> part(s) <verb>" summary, pluralizing "part" based on the
 * count. Returns null when the count is missing so the caller can fall back.
 */
function partsSummary(count: string | undefined, verb: string): string | null {
    if (!count) return null;
    const noun = count.trim() === '1' ? 'part' : 'parts';
    return `${count} ${noun} ${verb}`;
}

/**
 * Single-line human summary surfaced under the title (e.g. "35 parts ordered").
 * Returns null when the event has no concise narrative summary; in that case the
 * compartmentalized metadata box (if any) carries the structured detail instead.
 */
function getDetailsLine(log: EnrichedActivityLogEntry): string | null {
    const m = log.metadata as Record<string, string | undefined> | undefined;
    if (!m) return null;

    const legacyTagChange = getLegacyTagChangeFromDetailsEdited(log);
    if (legacyTagChange) return legacyTagChange.action;

    switch (log.eventType) {
        case 'orders-complete':
        case 'place-order':
            return partsSummary(m['Parts Ordered'], 'ordered');
        case 'quote-request-submitted':
        case 'request-quotes':
            return partsSummary(m['Parts'], 'requested');
        case 'quotes-complete':
            return partsSummary(m['Quoted'], 'quoted');
        case 'order-placement-requested':
            return partsSummary(m['Parts'], 'requested');
        case 'quoting-skipped':
            return partsSummary(m['Parts'], 'skipped');
        case 'reset-to-partbay-selections':
            return partsSummary(m['Parts Reset'], 'reset to Yaxle selections');
        case 'parts-received':
            return partsSummary(m['Parts Count'], 'received');
        case 'parts-unreceived':
            return partsSummary(m['Parts Count'], 'un-received');
        case 'order-tag-changed':
            return m['Action'] || null;
        case 'workflow-cancelled':
            return m['Workflow'] || null;
        case 'part-included':
        case 'part-excluded':
        case 'part-marked-ordered':
        case 'part-unmarked-ordered':
            return m['Part'] || m['Parts'] || null;
        case 'part-excluded-from-quoting':
        case 'part-quoting-restored':
            return m['Part'] || null;
        default:
            return null;
    }
}

type InlineBadge =
    | { text: string; colorScheme: string; source?: undefined; }
    | { source: ActivitySourceBadgeType; text?: undefined; colorScheme?: undefined; };

/**
 * Small badges rendered alongside the details line: identifies the upstream
 * system / context (Source, Selection Source) or the tag being added/removed
 * for tag events. Type badges for part-included / part-excluded are also
 * surfaced here so the row reads as "Part X · A/M" without nesting a box.
 */
function getInlineBadges(log: EnrichedActivityLogEntry): InlineBadge[] {
    const m = log.metadata as Record<string, string | undefined> | undefined;
    if (!m) return [];

    const badges: InlineBadge[] = [];
    if (m['Source']) {
        const sourceBadge = toSourceBadgeType(m['Source']);
        if (sourceBadge) {
            badges.push({ source: sourceBadge });
        } else {
            badges.push({
                text: m['Source'],
                colorScheme: getSourceColorScheme(m['Source']),
            });
        }
    }
    if (log.eventType === 'order-tag-changed' && m['Tag']) {
        badges.push({ text: m['Tag'], colorScheme: getOrderTagColorScheme(m['Tag']) });
    }
    const legacyTagChange = getLegacyTagChangeFromDetailsEdited(log);
    if (legacyTagChange) {
        badges.push({
            text: legacyTagChange.tag,
            colorScheme: getOrderTagColorScheme(legacyTagChange.tag),
        });
    }
    if (m['Selection Source']) {
        badges.push({
            text: m['Selection Source'],
            colorScheme: m['Selection Source'] === 'Flagged Review' ? 'yellow' : 'green',
        });
    }
    // Surface Type as an inline badge for every event except part-selections-changed,
    // which already pairs Old Type / Type with the supplier rows inside the structured
    // box. This matches the legacy behavior of always rendering Type whenever the
    // backend attaches it, so future enrichment additions (e.g. Type on flag-removed)
    // are surfaced automatically instead of being silently dropped.
    if (m['Type'] && log.eventType !== 'part-selections-changed') {
        badges.push({ text: m['Type'], colorScheme: getTypeColorScheme(m['Type']) });
    }
    return badges;
}

function formatListPriceChange(value: string): string {
    return value
        .split('→')
        .map((part) => {
            const trimmed = part.trim();
            if (!trimmed || trimmed.startsWith('$')) return trimmed;
            const amount = Number(trimmed.replace(/,/g, ''));
            return Number.isFinite(amount) ? formatPrice(amount) : `$${trimmed}`;
        })
        .join(' → ');
}

interface StructuredRow {
    label: string;
    value: string;
    partNumber?: string;
    badge?: { text: string; colorScheme: string; };
    /** Keep the full value visible instead of ellipsizing a single line. */
    wrap?: boolean;
}

/**
 * The backend's `formatPartsList` returns `"<title> (<partNumber>)"` for a
 * single part and a comma-joined version for multiple parts. When there is
 * exactly one part, peel the trailing `(partNumber)` off so we can render the
 * title in the value column and the part number alongside the copy button —
 * matching how `Part` rows are surfaced for `part-selections-changed`. Multi-
 * part values pass through untouched (the copy button only makes sense for a
 * single part number).
 */
function splitSinglePartLabel(value: string): { title: string; partNumber?: string; } {
    if (value.includes(',')) return { title: value };
    const match = value.match(/^(.+) \(([^()]+)\)\s*$/);
    if (match) return { title: match[1], partNumber: match[2] };
    return { title: value };
}

/**
 * Builds the rows for the compartmentalized partbay-blue inner box.
 * Returns null when the event has no rich structured payload worth nesting.
 *
 * For supplier swaps we render Part / From / To, pairing the type badge with
 * its supplier so the OEM ↔ A/M context lands next to the right name.
 */
function getStructuredRows(log: EnrichedActivityLogEntry): StructuredRow[] | null {
    const m = log.metadata as Record<string, string | undefined> | undefined;
    if (!m) return null;

    switch (log.eventType) {
        case 'part-selections-changed': {
            const rows: StructuredRow[] = [];
            if (m['Part']) {
                rows.push({ label: 'Part', value: m['Part'], partNumber: m['Part Number'] });
            }
            if (m['Old Supplier']) {
                const oldType = m['Old Type'] || m['Type'];
                rows.push({
                    label: 'From',
                    value: m['Old Supplier'],
                    badge: oldType ? { text: oldType, colorScheme: getTypeColorScheme(oldType) } : undefined,
                });
            }
            if (m['New Supplier']) {
                rows.push({
                    label: 'To',
                    value: m['New Supplier'],
                    badge: m['Type'] ? { text: m['Type'], colorScheme: getTypeColorScheme(m['Type']) } : undefined,
                });
            }
            return rows.length > 0 ? rows : null;
        }

        case 'order-details-edited': {
            if (getLegacyTagChangeFromDetailsEdited(log)) return null;

            const rows: StructuredRow[] = [];
            for (const [key, value] of Object.entries(m)) {
                if (!value) continue;
                rows.push({ label: key, value });
            }
            return rows.length > 0 ? rows : null;
        }

        case 'supplier-changes-accepted':
        case 'supplier-order-confirmed': {
            const rows: StructuredRow[] = [];
            for (const [key, value] of Object.entries(m)) {
                if (!value) continue;
                rows.push({ label: key, value });
            }
            return rows.length > 0 ? rows : null;
        }

        case 'parts-received':
        case 'parts-unreceived': {
            // Counts already headline the details line; the box carries the
            // vendor, part list, and receiver.
            const rows: StructuredRow[] = [];
            if (m['Supplier']) rows.push({ label: 'Supplier', value: m['Supplier'] });
            if (m['Parts']) {
                const { title, partNumber } = splitSinglePartLabel(m['Parts']);
                rows.push({ label: 'Parts', value: title, partNumber });
            }
            if (m['Received By']) rows.push({ label: 'Received By', value: m['Received By'] });
            return rows.length > 0 ? rows : null;
        }

        case 'list-price-adjusted': {
            const rows: StructuredRow[] = [];
            if (m['Part']) {
                rows.push({ label: 'Part', value: m['Part'], partNumber: m['Part Number'] });
            }
            if (m['List Price']) {
                rows.push({ label: 'List Price', value: formatListPriceChange(m['List Price']) });
            }
            return rows.length > 0 ? rows : null;
        }

        case 'option-note-changed': {
            const rows: StructuredRow[] = [];
            if (m['Part']) {
                rows.push({ label: 'Part', value: m['Part'], partNumber: m['Part Number'] });
            }
            if (m['Destination']) {
                rows.push({ label: 'Destination', value: m['Destination'] });
            }
            if (m['Previous Note']) {
                rows.push({ label: 'Previous Note', value: m['Previous Note'] });
            }
            if (m['Note']) {
                rows.push({ label: 'Note', value: m['Note'] });
            }
            if (m['Change']) {
                rows.push({ label: 'Change', value: m['Change'] });
            }
            return rows.length > 0 ? rows : null;
        }

        case 'flag-removed':
        case 'supplier-rejected':
        case 'supplier-cancelled-parts':
        case 'supplier-returned-parts': {
            const skip = new Set(['Type', 'Old Type', 'Source', 'Selection Source', 'Part Number', 'Flag Type']);
            const rows: StructuredRow[] = [];
            for (const [key, value] of Object.entries(m)) {
                if (!value || skip.has(key)) continue;
                if (key === 'Part') {
                    rows.push({ label: 'Part', value, partNumber: m['Part Number'] });
                } else if (key === 'Parts') {
                    const { title, partNumber } = splitSinglePartLabel(value);
                    rows.push({ label: 'Parts', value: title, partNumber });
                } else {
                    rows.push({ label: key, value });
                }
            }
            return rows.length > 0 ? rows : null;
        }

        case 'shop-cancel-or-return': {
            const rows: StructuredRow[] = [];
            const seen = new Set(['Type', 'Old Type', 'Source', 'Selection Source', 'Part Number', 'Flag Type']);
            const push = (key: string, wrap = false) => {
                const value = m[key];
                if (!value) return;
                seen.add(key);
                if (key === 'Parts') {
                    const { title, partNumber } = splitSinglePartLabel(value);
                    rows.push({ label: 'Parts', value: title, partNumber });
                } else {
                    rows.push({ label: key, value, wrap });
                }
            };
            // Reason is the option chosen in CancelReturnPartsModal (plus an
            // "Other" note). Keep it next to the action so it isn't buried.
            push('Action');
            push('Reason', true);
            push('Supplier');
            push('Parts Count');
            push('Parts');
            push('Re-Order');
            for (const [key, value] of Object.entries(m)) {
                if (!value || seen.has(key)) continue;
                rows.push({ label: key, value });
            }
            return rows.length > 0 ? rows : null;
        }

        default:
            return null;
    }
}

function groupByDate(logs: EnrichedActivityLogEntry[]): Map<string, EnrichedActivityLogEntry[]> {
    const groups = new Map<string, EnrichedActivityLogEntry[]>();
    for (const log of logs) {
        const key = formatDateGroup(log.timestamp);
        const group = groups.get(key);
        if (group) {
            group.push(log);
        } else {
            groups.set(key, [log]);
        }
    }
    return groups;
}

const ActivityLogDrawer: React.FC<ActivityLogDrawerProps> = ({ isOpen, onClose, orderId, hideOverlay = false }) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<ActivityFeedFiltersValue>({ ...EMPTY_ACTIVITY_FEED_FILTERS });
    const { copy, isCopied } = useCopyToClipboard();
    const filterUsers = useActivityFeedFilterUsers();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch((prev) => {
                if (prev !== search.trim()) setPage(1);
                return search.trim();
            });
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const { data: activityData, isLoading, isError, isFetching, refetch } = useGetActivityLogs(
        orderId,
        page,
        PAGE_SIZE,
        isOpen,
        debouncedSearch,
        filters,
        mapOrderEventTypesForApi,
    );

    useEffect(() => {
        if (isOpen) {
            void refetch();
        }
    }, [isOpen, refetch]);

    const handleFiltersChange = (next: ActivityFeedFiltersValue) => {
        setFilters(next);
        setPage(1);
    };

    const logs = useMemo(() => activityData?.logs ?? [], [activityData?.logs]);
    const totalCount = activityData?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const grouped = useMemo(() => groupByDate(logs), [logs]);

    const renderLogCard = (log: EnrichedActivityLogEntry) => {
        const displayType = getDisplayEventType(log);
        const detailsLine = getDetailsLine(log);
        const inlineBadges = getInlineBadges(log);
        const structuredRows = getStructuredRows(log);
        // Align every value column inside the card by sizing the label
        // column to the longest label. ~7px per uppercase 11px char +
        // a small breathing buffer matches the rendered metrics here.
        const structuredLabelMinW = structuredRows
            ? `${Math.max(...structuredRows.map((r) => r.label.length)) * 7 + 8}px`
            : undefined;
        // Title-only events (e.g. Order Completed, Order Archived) read better when
        // the icon and title row are vertically centered inside the card.
        const hasSubcontent = !!detailsLine || inlineBadges.length > 0 || !!structuredRows;
        // Actions Carl took on the user's behalf swap the event icon for the Carl
        // sparkle inside a blue-ringed circle, hover-labeled "w/ Carl Agent".
        const viaCarl = log.source === 'carl-agent';
        const iconCircle = (
            <Flex
                w="32px"
                h="32px"
                borderRadius="full"
                bg={viaCarl ? 'brand.subtle' : (EVENT_TYPE_ICON_BG[displayType] ?? 'bg.subtle')}
                align="center"
                justify="center"
                flexShrink={0}
                mt={hasSubcontent ? 0.5 : 0}
            >
                <EventTypeIcon
                    eventType={displayType}
                    viaCarl={viaCarl}
                    size="1rem"
                />
            </Flex>
        );
        return (
            <Box
                bg="white"
                py={3}
                borderBottom="1px solid"
                borderColor="gray.100"
            >
                <Flex align={hasSubcontent ? 'flex-start' : 'center'} gap={3}>
                    {/* Icon */}
                    {viaCarl ? (
                        <Tooltip
                            label="w/ Carl Agent"
                            placement="top"
                            hasArrow
                            openDelay={200}
                            bg="gray.900"
                            color="white"
                            fontSize="xs"
                            borderRadius="md"
                            px={2}
                            py={1.5}
                        >
                            {iconCircle}
                        </Tooltip>
                    ) : (
                        iconCircle
                    )}

                    {/* Content */}
                    <Box flex={1} minW={0}>
                        {/* Title row: action · actor   /   time */}
                        <Flex align="center" justify="space-between" gap={2} minW={0}>
                            <HStack spacing={1.5} minW={0} flex={1}>
                                <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="gray.800"
                                    flexShrink={0}
                                    lineHeight="1.3"
                                >
                                    {getActionLabel(log)}
                                </Text>
                                <Text fontSize="sm" color="gray.400" flexShrink={0} lineHeight="1.3">
                                    ·
                                </Text>
                                <Text
                                    fontSize="sm"
                                    color="gray.600"
                                    isTruncated
                                    minW={0}
                                    lineHeight="1.3"
                                >
                                    {getActorName(log)}
                                </Text>
                            </HStack>
                            <Text
                                fontSize="xs"
                                color="gray.500"
                                flexShrink={0}
                                whiteSpace="nowrap"
                            >
                                {formatTime(log.timestamp)}
                            </Text>
                        </Flex>

                        {/* Details line + inline badges */}
                        {(detailsLine || inlineBadges.length > 0) && (
                            <Flex
                                mt={1.5}
                                align="center"
                                wrap="wrap"
                                rowGap={1}
                                columnGap={2}
                            >
                                {detailsLine && (
                                    <Text fontSize="13px" color="gray.600" lineHeight="1.3">
                                        {detailsLine}
                                    </Text>
                                )}
                                {inlineBadges.map((b, i) => (
                                    b.source ? (
                                        <SourceBadge key={`${log._id}-inline-${i}`} source={b.source} />
                                    ) : (
                                        <Badge
                                            key={`${log._id}-inline-${i}`}
                                            colorScheme={b.colorScheme}
                                            variant="subtle"
                                            fontSize="11px"
                                            px={1.5}
                                            borderRadius="sm"
                                            textTransform="none"
                                            fontWeight="semibold"
                                        >
                                            {b.text}
                                        </Badge>
                                    )
                                ))}
                            </Flex>
                        )}

                        {/* Compartmentalized inner box (partbay-blue tinted) */}
                        {structuredRows && (
                            <Box
                                mt={2.5}
                                bg="brand.subtle"
                                borderWidth="1px"
                                borderColor="brand.muted"
                                borderRadius="md"
                                px={3}
                                py={2}
                            >
                                <VStack spacing={1.5} align="stretch">
                                    {structuredRows.map((row, idx) => (
                                        <Flex
                                            key={`${log._id}-row-${idx}`}
                                            gap={3}
                                            align={row.wrap ? 'flex-start' : 'center'}
                                            minW={0}
                                        >
                                            <Text
                                                fontSize="11px"
                                                color="fg.muted"
                                                fontWeight="semibold"
                                                textTransform="uppercase"
                                                letterSpacing="wide"
                                                minW={structuredLabelMinW}
                                                flexShrink={0}
                                                whiteSpace="nowrap"
                                            >
                                                {row.label}
                                            </Text>
                                            <Tooltip
                                                label={row.value}
                                                placement="top"
                                                hasArrow
                                                openDelay={400}
                                                bg="gray.900"
                                                color="white"
                                                fontSize="xs"
                                                borderRadius="md"
                                                px={2}
                                                py={1.5}
                                            >
                                                <Text
                                                    fontSize="13px"
                                                    color="fg.default"
                                                    flex={1}
                                                    minW={0}
                                                    isTruncated={!row.wrap}
                                                    whiteSpace={row.wrap ? 'normal' : undefined}
                                                    lineHeight="1.35"
                                                >
                                                    {row.value}
                                                </Text>
                                            </Tooltip>
                                            {row.partNumber && (
                                                <HStack spacing={1} flexShrink={0}>
                                                    <CopyTooltip isCopied={isCopied(`part-${log._id}`)}>
                                                        <Text fontSize="11px" color="gray.500" fontFamily="mono">
                                                            {row.partNumber}
                                                        </Text>
                                                    </CopyTooltip>
                                                    <Box
                                                        as="button"
                                                        display="inline-flex"
                                                        alignItems="center"
                                                        onClick={() => copy(row.partNumber!, `part-${log._id}`)}
                                                        cursor="pointer"
                                                        _hover={{ color: 'gray.700' }}
                                                        color="gray.400"
                                                        aria-label="Copy part number"
                                                    >
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                                    </Box>
                                                </HStack>
                                            )}
                                            {row.badge && (
                                                <Badge
                                                    colorScheme={row.badge.colorScheme}
                                                    variant="subtle"
                                                    fontSize="11px"
                                                    px={1.5}
                                                    borderRadius="sm"
                                                    flexShrink={0}
                                                    textTransform="none"
                                                    fontWeight="semibold"
                                                >
                                                    {row.badge.text}
                                                </Badge>
                                            )}
                                        </Flex>
                                    ))}
                                </VStack>
                            </Box>
                        )}

                        {/* Shop-side actions pushed from the shop's management system (Nexsyis). */}
                        {log.source === 'nexsyis' && (
                            <Text mt={1.5} fontSize="xs" color="fg.muted">
                                Initiated from NEX
                            </Text>
                        )}
                    </Box>
                </Flex>
            </Box>
        );
    };

    const timelineGroups: TimelineGroupData[] = Array.from(grouped.entries()).map(([dateLabel, logs]) => ({
        key: dateLabel,
        label: dateLabel,
        rightLabel: `${logs.length} event${logs.length === 1 ? '' : 's'}`,
        items: logs.map((log) => ({
            key: log._id,
            // Carl-initiated rows override the timeline node/line to brand blue.
            nodeColor: log.source === 'carl-agent'
                ? 'brand.solid'
                : (EVENT_TYPE_COLORS[getDisplayEventType(log)] ?? 'brand.solid'),
            content: renderLogCard(log),
        })),
    }));

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title="Activity Logs"
            hideOverlay={hideOverlay}
            contentProps={{
                overflow: 'hidden',
                containerProps: hideOverlay ? SIDE_DRAWER_CONTAINER_PROPS : undefined,
                motionProps: INSTANT_SIDE_DRAWER_MOTION_PROPS,
            }}
            bodyProps={{ px: 5, py: 4, pt: 4, bg: 'white' }}
            headerBelow={
                <Box px={5} py={3} borderBottom="1px solid" borderColor="border.subtle">
                    <ActivityFeedFilters
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        eventTypeGroups={EVENT_TYPE_FILTER_GROUPS}
                        users={filterUsers}
                        searchSlot={(
                            <TextField
                                size="sm"
                                placeholder="Search activity..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                h="32px"
                            />
                        )}
                    />
                </Box>
            }
            bottomBar={
                totalPages > 1 ? (
                    <HStack justify="center" spacing={3} py={2} bg="bg.surface" borderTop="1px solid" borderColor="border.subtle">
                        <Button
                            size="xs"
                            variant="outlineGray"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            isDisabled={page <= 1 || isFetching}
                        >
                            Previous
                        </Button>
                        <Text fontSize="xs" color="fg.muted">
                            Page {page} of {totalPages}
                        </Text>
                        <Button
                            size="xs"
                            variant="outlineGray"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            isDisabled={page >= totalPages || isFetching}
                        >
                            Next
                        </Button>
                    </HStack>
                ) : undefined
            }
        >
            {isLoading ? (
                <Center py={10}>
                    <Spinner color="brand.solid" />
                </Center>
            ) : isError ? (
                <Center py={10} flexDirection="column">
                    <Text fontSize="sm" color="red.500" mb={3}>
                        Failed to load activity logs
                    </Text>
                    <Button size="sm" variant="outline" colorScheme="red" onClick={() => refetch()}>
                        Retry
                    </Button>
                </Center>
            ) : logs.length === 0 ? (
                <Center py={10}>
                    <Text fontSize="sm" color="fg.muted" fontStyle="italic">
                        {debouncedSearch || hasActiveActivityFeedFilters(filters)
                            ? 'No activity matches your filters'
                            : 'No activity logged yet'}
                    </Text>
                </Center>
            ) : (
                <Timeline groups={timelineGroups} />
            )}
        </AppDrawer>
    );
};

export default ActivityLogDrawer;
